// modules/reporteFacturas/pages/ReporteFacturasIndexPage.jsx
// Punto de entrada de "Reportes de Factura" — elegís sucursal/mes/año una
// vez y despues el reporte puntual (cada uno abre en su propia pagina con
// esos filtros ya cargados). Agregar un reporte nuevo es solo sumar una
// entrada a REPORTES, no hace falta tocar el resto de la pagina.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, TextField, MenuItem, Typography, Paper, Tabs, Tab, Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentsIcon from "@mui/icons-material/Payments";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TuneIcon from "@mui/icons-material/Tune";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Todo"];

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

// Titulo/subtitulo propio por pestaña — cada una es un grupo de reportes
// distinto, no tiene sentido compartir un solo encabezado generico.
const TITULOS_PESTANA = [
  { title: "Reportes de Facturas", subtitle: "Elige sucursal, mes y año, y después el reporte que quieres ver" },
  { title: "Reportes de Pagos", subtitle: "Reportes relacionados a pagos de clientes" },
  { title: "Reportes de Cierre", subtitle: "Reportes de cierre de período" },
];

// Sumar un reporte nuevo = agregar una entrada aca, con su ruta y sus
// parametros propios (ej. otro filtro extra en vez de incluirPrefacturas).
const REPORTES = [
  {
    key: "todas-prefacturas",
    titulo: "Reporte Todas Las Facturas y Pre-Facturas",
    descripcion: "Listado de facturas y prefacturas generadas en el mes seleccionado",
    ruta: "/reporte-facturas/total",
    params: { preFac: "todos" },
  },
  {
    key: "todas",
    titulo: "Reporte Todas Las Facturas",
    descripcion: "Listado de facturas generadas en el mes seleccionado",
    ruta: "/reporte-facturas/total",
    params: { preFac: "no" },
  },
  {
    key: "solo-prefacturas",
    titulo: "Reporte Solo Pre-Facturas",
    descripcion: "Listado de prefacturas (todavía sin emitir) generadas en el mes seleccionado",
    ruta: "/reporte-facturas/total",
    params: { preFac: "si" },
  },
  {
    key: "por-codigo",
    titulo: "Reporte por Código de Producción",
    descripcion: "Facturas repartidas por los códigos de Facturación admin (actividad económica e item): cuánto de cada factura corresponde a cada código",
    ruta: "/reporte-facturas/por-codigo",
    params: { preFac: "no" },
  },
  {
    key: "detalle-interno",
    titulo: "Reporte de Detalle Interno",
    descripcion: "Facturas y pre-facturas con conceptos internos (AVL_NACIONAL, TKR_SERVICIO…): suma de la línea de % alto y, aparte, lo de % bajo que no entra al total",
    ruta: "/reporte-facturas/detalle-interno",
    params: { preFac: "todos" },
  },
  {
    key: "mora",
    titulo: "Reporte por Factura en Mora",
    descripcion: "Listado de todas las facturas y prefacturación sin pago total (se muestra todo sin importar las fechas)",
    ruta: "/reporte-facturas/mora",
    pasarFiltros: false,
  },
  {
    key: "impuestos",
    titulo: "Reporte de Impuestos",
    descripcion: "Facturas emitidas al SIAT (vía Hippo) — filtra por rango de fechas en su propia pantalla",
    ruta: "/reporte-impuestos",
    pasarFiltros: false,
  },
  {
    key: "libro-ventas",
    titulo: "Libro de Ventas",
    descripcion: "Facturas emitidas al SIAT en formato libro de ventas IVA — filtra por rango de fechas en su propia pantalla",
    ruta: "/reporte-libro-ventas",
    pasarFiltros: false,
  },
  {
    key: "por-anio",
    titulo: "Reporte por Año",
    descripcion: "Totales por mes y tipo de servicio de facturas emitidas",
    ruta: "/reporte-facturas/por-anio",
    params: {},
  },
  {
    key: "por-anio-mes",
    titulo: "Reporte por Año y Mes",
    descripcion: "Totales pre facturados, facturados y cobrados por mes",
    ruta: "/reporte-facturas/por-anio-mes",
    pasarFiltros: false,
  },
];

const REPORTES_PAGOS = [
  {
    key: "tipo-banco",
    titulo: "Reporte por Tipo, Moneda y Banco",
    descripcion: "Cuánto se cobró por tipo de ingreso y banco de depósito — resumen mensual/anual y detalle mes a mes",
    ruta: "/reporte-pagos/tipo-banco",
    pasarFiltros: false,
  },
  {
    key: "detalle-cobros",
    titulo: "Detalle de Cobros",
    descripcion: "Cobros de facturas y franquicias, fila por fila — filtrable por sucursal, mes/año y banco de depósito",
    ruta: "/reporte-pagos/detalle-cobros",
    pasarFiltros: false,
  },
  {
    key: "por-servicio",
    titulo: "Reporte por Servicio",
    descripcion: "Cuánto se cobró por tipo de servicio (TKR, AVL, Scada, Seguridad, etc.) — resumen y detalle mes a mes",
    ruta: "/reporte-pagos/por-servicio",
    pasarFiltros: false,
  },
  {
    key: "detalle-tipo",
    titulo: "Detallado por Tipo",
    descripcion: "Cobros de Franquicia, Intercambio de Servicios o Castigo, fila por fila",
    ruta: "/reporte-pagos/detalle-tipo",
    pasarFiltros: false,
  },
];

const REPORTES_CIERRE = [
  {
    key: "cierre",
    titulo: "Cierre de Facturación",
    descripcion: "Totales facturados por mes y sucursal, cierre mensual e historial de meses cerrados",
    ruta: "/reporte-cierre",
    pasarFiltros: false,
  },
];

// Se guarda la pestaña activa para que, al volver desde un reporte puntual
// (que siempre navega de vuelta a "/reporte-facturas" a secas), no se pierda
// si veniamos de Facturas, Pagos o Cierre.
const TAB_STORAGE_KEY = "reporteFacturas.pestanaActiva";

// Los reportes de facturas se agrupan por lo que responden; cada grupo tiene su color e ícono
const GRUPOS_FACTURAS = [
  { titulo: "Listados de facturas", nota: "una fila por factura", color: "#0b6e6e", Icono: ListAltIcon, claves: ["todas-prefacturas", "todas", "solo-prefacturas", "mora"] },
  { titulo: "Por código y detalle interno", nota: "cómo se reparte lo facturado", color: "#6a1b9a", Icono: AccountTreeIcon, claves: ["por-codigo", "detalle-interno"] },
  { titulo: "Totales por periodo", nota: "resúmenes por año y mes", color: "#1565c0", Icono: CalendarMonthIcon, claves: ["por-anio", "por-anio-mes"] },
  { titulo: "Impuestos", nota: "facturas emitidas al SIAT", color: "#c77700", Icono: AccountBalanceIcon, claves: ["impuestos", "libro-ventas"] },
];

// Tarjeta de un reporte: toda la tarjeta es clicable
const TarjetaReporte = ({ reporte, color, onAbrir }) => {
  const usaFiltros = reporte.pasarFiltros !== false;
  return (
    <Paper
      variant="outlined" role="button" tabIndex={0} onClick={() => onAbrir(reporte)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onAbrir(reporte))}
      sx={{
        p: 1.5, cursor: "pointer", display: "flex", flexDirection: "column", gap: 0.75, borderLeft: 3, borderLeftColor: color,
        transition: "box-shadow .15s, border-color .15s",
        "&:hover": { boxShadow: 3, borderColor: color }, "&:focus-visible": { outline: "2px solid", outlineColor: color },
      }}
    >
      <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, lineHeight: 1.25 }}>{reporte.titulo}</Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", lineHeight: 1.35, flex: 1 }}>{reporte.descripcion}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
        {usaFiltros ? (
          <Chip size="small" icon={<TuneIcon />} label="Usa los filtros de arriba" sx={{ height: 20, fontSize: 10.5, bgcolor: alpha(color, 0.1), color, "& .MuiChip-icon": { color, fontSize: 13 } }} />
        ) : (
          <Chip size="small" variant="outlined" label="Tiene sus propios filtros" sx={{ height: 20, fontSize: 10.5 }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" endIcon={<ArrowForwardIcon />} sx={{ textTransform: "none", fontWeight: 700, color }} onClick={(e) => { e.stopPropagation(); onAbrir(reporte); }}>
          Abrir
        </Button>
      </Box>
    </Paper>
  );
};

const GrupoReportes = ({ titulo, nota, color, Icono, reportes, onAbrir }) => (
  <Box sx={{ mb: 2.25 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9, pb: 0.4, borderBottom: 1, borderColor: "divider" }}>
      <Box sx={{ width: 26, height: 26, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: alpha(color, 0.14), color }}><Icono sx={{ fontSize: 16 }} /></Box>
      <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color }}>{titulo}</Typography>
      {nota && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{nota}</Typography>}
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
      {reportes.map((r) => <TarjetaReporte key={r.key} reporte={r} color={color} onAbrir={onAbrir} />)}
    </Box>
  </Box>
);

const ReporteFacturasIndexPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(() => {
    const guardada = Number(sessionStorage.getItem(TAB_STORAGE_KEY));
    return [0, 1, 2].includes(guardada) ? guardada : 0;
  });
  const [sucursal, setSucursal] = useState(() => normalizarSucursal(user?.sucursal));
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(anioActual);

  const handleMostrar = (reporte) => {
    if (reporte.pasarFiltros === false) {
      navigate(reporte.ruta);
      return;
    }
    const query = new URLSearchParams({
      sucursal, mes: String(mes), year: String(year), ...reporte.params,
    });
    navigate(`${reporte.ruta}?${query.toString()}`);
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={TITULOS_PESTANA[tab].title}
        subtitle={TITULOS_PESTANA[tab].subtitle}
        backTo="/dashboard"
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              sessionStorage.setItem(TAB_STORAGE_KEY, String(v));
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Facturas" />
            <Tab label="Pagos" />
            <Tab label="Cierre" />
          </Tabs>
        </Paper>

        <Box sx={{ maxWidth: 1080, mx: "auto" }}>
          {tab === 0 && (
            <>
              {/* Periodo y sucursal: se aplican a los reportes que dicen "Usa los filtros de arriba" */}
              <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 1.5, py: 0.5, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>Periodo y sucursal</Typography>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>se aplican a los reportes marcados con “Usa los filtros de arriba”</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", p: 1.5 }}>
                  <TextField select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 170 }} value={sucursal} onChange={(e) => setSucursal(e.target.value)}>
                    {SUCURSALES.map((x) => <MenuItem key={x} value={x} sx={{ fontSize: "0.85rem" }}>{x}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Mes" sx={{ ...compactFieldSx, width: 190 }} value={mes} onChange={(e) => setMes(e.target.value)}>
                    {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {ANIOS.map((x) => <MenuItem key={x} value={x} sx={{ fontSize: "0.85rem" }}>{x}</MenuItem>)}
                  </TextField>
                </Box>
              </Paper>

              {GRUPOS_FACTURAS.map((g) => (
                <GrupoReportes key={g.titulo} titulo={g.titulo} nota={g.nota} color={g.color} Icono={g.Icono} onAbrir={handleMostrar}
                  reportes={g.claves.map((k) => REPORTES.find((r) => r.key === k)).filter(Boolean)} />
              ))}
              {/* Cualquier reporte nuevo que todavía no esté en un grupo aparece aquí, para que no se pierda */}
              {(() => {
                const enGrupo = new Set(GRUPOS_FACTURAS.flatMap((g) => g.claves));
                const sueltos = REPORTES.filter((r) => !enGrupo.has(r.key));
                return sueltos.length ? <GrupoReportes titulo="Otros reportes" color="#546e7a" Icono={AssessmentIcon} reportes={sueltos} onAbrir={handleMostrar} /> : null;
              })()}
            </>
          )}

          {tab === 1 && <GrupoReportes titulo="Reportes de pagos" nota="cobros por banco, servicio y tipo" color="#2e7d32" Icono={PaymentsIcon} reportes={REPORTES_PAGOS} onAbrir={handleMostrar} />}
          {tab === 2 && <GrupoReportes titulo="Cierre de facturación" nota="cierres mensuales" color="#c62828" Icono={EventAvailableIcon} reportes={REPORTES_CIERRE} onAbrir={handleMostrar} />}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasIndexPage;
